import { useState, useEffect } from "react";

const GLITCH_CHARS = "█▓▒░▄▀▌▐ΔΞΨΩ★⚡×ØÆµ§¥£¢$#@?%/\\+=~<>";

interface GlitchLetterProps {
  char: string;
  glitchClassName?: string;
  forceGlitchTrigger?: number;
}

function GlitchLetter({ char, glitchClassName = "", forceGlitchTrigger }: GlitchLetterProps) {
  const [displayChar, setDisplayChar] = useState(char);
  const [isGlitched, setIsGlitched] = useState(false);

  const triggerGlitch = () => {
    if (char === " ") return;
    setIsGlitched(true);

    let flashCount = 0;
    const maxFlashes = 3 + Math.floor(Math.random() * 3); // 3 to 5 flashes

    const flash = () => {
      flashCount++;
      if (flashCount < maxFlashes) {
        setDisplayChar(GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)]);
        setTimeout(flash, 50 + Math.random() * 50);
      } else {
        setDisplayChar(char);
        setIsGlitched(false);
      }
    };

    flash();
  };

  useEffect(() => {
    if (forceGlitchTrigger) {
      triggerGlitch();
    }
  }, [forceGlitchTrigger]);

  return (
    <span
      onMouseEnter={triggerGlitch}
      className={
        isGlitched
          ? `inline-block transition-all duration-75 scale-y-110 filter brightness-110 cursor-default select-none ${glitchClassName}`
          : "cursor-default select-none"
      }
      style={isGlitched ? { textShadow: "0 0 8px currentColor" } : undefined}
    >
      {displayChar}
    </span>
  );
}

interface GlitchTextProps {
  text: string;
  className?: string;
  glitchClassName?: string;
}

export function GlitchText({ text, className = "", glitchClassName = "" }: GlitchTextProps) {
  const [triggers, setTriggers] = useState<Record<number, number>>({});

  useEffect(() => {
    let timeoutId: any;

    const runGlitchCycle = () => {
      const length = text.length;
      if (length === 0) return;

      const numToGlitch = Math.max(1, Math.floor(Math.random() * Math.max(2, length * 0.15)));
      const nonSpaceIndices = [...text].map((c, i) => (c !== " " ? i : -1)).filter((i) => i !== -1);

      if (nonSpaceIndices.length === 0) return;

      setTriggers((prev) => {
        const nextTriggers = { ...prev };
        for (let i = 0; i < numToGlitch; i++) {
          const randIdx = nonSpaceIndices[Math.floor(Math.random() * nonSpaceIndices.length)];
          nextTriggers[randIdx] = Date.now();
        }
        return nextTriggers;
      });

      scheduleNext();
    };

    const scheduleNext = () => {
      const delay = 3000 + Math.random() * 4000;
      timeoutId = setTimeout(runGlitchCycle, delay);
    };

    scheduleNext();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [text]);

  const words = text.split(" ");

  const getAbsoluteIndex = (wordIndex: number, charIndex: number): number => {
    let index = 0;
    for (let i = 0; i < wordIndex; i++) {
      index += words[i].length + 1; // +1 for space
    }
    return index + charIndex;
  };

  return (
    <span className={className}>
      {words.map((word, wordIndex) => (
        <span key={wordIndex}>
          <span className="inline-block">
            {word.split("").map((char, charIndex) => {
              const absoluteIndex = getAbsoluteIndex(wordIndex, charIndex);
              return (
                <GlitchLetter
                  key={charIndex}
                  char={char}
                  glitchClassName={glitchClassName}
                  forceGlitchTrigger={triggers[absoluteIndex]}
                />
              );
            })}
          </span>
          {wordIndex < words.length - 1 && " "}
        </span>
      ))}
    </span>
  );
}
